import { Plugins } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { useStore } from 'effector-react'
import { useEffect, useRef, useState } from 'react'
import { notificationsModel } from '~/entities/Notifications'
import { SMS_CODE_LENGTH, isMobileApp } from '~/shared/config/constants'
import { NotificationTokenTypes } from '~/shared/config/enums'
import { extractSMSCode } from '~/shared/lib/extractCode'
import type { PushNotificationMessage } from './types'

type UsePushCodeOptions = {
  codeLength?: number
  onCode: (code: string) => void
}

const setupListeners = async (
  handlePushNotification: (notification: PushNotificationMessage) => void,
  tokenType: NotificationTokenTypes | undefined,
) => {
  if (tokenType === NotificationTokenTypes.firebase) {
    await PushNotifications.addListener(
      'pushNotificationReceived',
      handlePushNotification,
    )
  }

  if (tokenType === NotificationTokenTypes.rustore) {
    await Plugins.RuStorePush.addListener(
      'pushReceived',
      handlePushNotification,
    )
  }
}

export const usePushCode = ({
  codeLength = SMS_CODE_LENGTH,
  onCode,
}: UsePushCodeOptions) => {
  const [error, setError] = useState<string | null>(null)
  const listenerInitialized = useRef(false)
  const onCodeRef = useRef(onCode)
  const tokenType = useStore(notificationsModel.$tokenType)

  useEffect(() => {
    onCodeRef.current = onCode
  }, [onCode])

  useEffect(() => {
    if (!isMobileApp || listenerInitialized.current) return

    const handlePushNotification = (notification: PushNotificationMessage) => {
      const message = notification.body || ''
      if (!message) return
      const code = extractSMSCode(message, codeLength)
      if (code) onCodeRef.current(code)
    }

    setupListeners(handlePushNotification, tokenType)
      .then(() => {
        listenerInitialized.current = true
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Ошибка при чтении push')
      })

    return () => {
      if (!listenerInitialized.current) return

      if (tokenType === NotificationTokenTypes.firebase) {
        PushNotifications.removeAllListeners()
      }

      if (tokenType === NotificationTokenTypes.rustore) {
        Plugins.RuStorePush.removeAllListeners()
      }
      listenerInitialized.current = false
    }
  }, [codeLength, tokenType])

  return { error }
}
